/**
 * Shared between the roster add / match check and the web client's add-provider
 * dialog and CSV import, which use it to offer the existing provider instead of
 * adding a duplicate. Error causes ride on BAD_REQUEST, which the tRPC error
 * formatter forwards to the client.
 */

/** The NPI or email already belongs to a provider the BU has; never added. */
export const ROSTER_IDENTITY_CONFLICT_ERROR_CODE = "ROSTER_IDENTITY_CONFLICT";

/** Providers with the same last name exist that the user has not reviewed. */
export const ROSTER_NAME_MATCH_ERROR_CODE = "ROSTER_NAME_MATCH";

/** Where an existing provider sits on the BU's roster. */
export enum RosterProviderMatchStatus {
  Active = "active",
  Invited = "invited",
  /** The organization ended it, so the latest stint can be reinstated. */
  Ended = "ended",
  /** The provider ended it; only they can rejoin, through an invitation. */
  EndedByProvider = "ended-by-provider",
}

/** Which identifier made an add a definite duplicate. */
export enum RosterIdentityKind {
  Npi = "npi",
  Email = "email",
}

export type RosterIdentityConflictCause = {
  code: typeof ROSTER_IDENTITY_CONFLICT_ERROR_CODE;
  matchedOn: RosterIdentityKind;
  status: RosterProviderMatchStatus;
  /** The live affiliation to open, when `status` is Active. */
  affiliationId: string | null;
  /** The ended stint to reinstate, when `status` is Ended. */
  endedAffiliationId: string | null;
};

/**
 * A provider the BU has with the same last name as the one being added. `id`
 * is what an add lists in `reviewedNameMatchIds` once the user has confirmed
 * this is a different person. A match outside the user's provider pools keeps
 * its id but shows no name and offers no action.
 */
export type RosterNameMatch = {
  id: string;
  status: RosterProviderMatchStatus;
  visible: boolean;
  firstName: string | null;
  lastName: string | null;
  affiliationId: string | null;
  endedAffiliationId: string | null;
};

/** One provider's result from `checkRosterProviders`. */
export type RosterProviderCheck = {
  /** Set when the add would be rejected outright; `message` says why. */
  identityConflict: (RosterIdentityConflictCause & { message: string }) | null;
  nameMatches: RosterNameMatch[];
};
